import React, {useEffect, useState} from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {
  ClosedAccount,
  closeAccountCopy,
  closeMyAccount,
  DELETE_CONFIRMATION_WORD,
  isCloseAccountError,
  isDeleteConfirmed,
  previewEraseOn,
  rememberClosedNotice,
} from "./account-closure.service";
import {isAuthRedirectError, signOutUser} from "../../shared/services/auth.service";
import {logError} from "../../shared/services/logger.service";
import {ConfirmItsYou} from "./ConfirmItsYou";

const RECAPTCHA_ID = 'close-account-recaptcha';
const TITLE_ID = 'close-account-title';

type Step = 'explain' | 'reauth' | 'confirm';

const explainBullets = (date: string) => [
  'Signs you out and stops you signing in again.',
  "Removes you from every team you're on.",
  `Permanently deletes your name, email, phone number, address, pay details and sign-in on ${date}. Until then, SavaQuan support can restore your account.`,
  "Leaves your teams' clients, estimates, jobs, invoices and photos in place. Where they name you, they'll show \"Former team member\".",
];

// Delete account, at the bottom of the account page (Account on the iOS and
// Android apps, which run the same three steps): what closing does, a fresh
// sign-in with the session's own method, then typing DELETE. The server
// closes the account at once and erases its personal details after 90 days,
// so the dialog states that date before anything happens.
export const CloseAccountDialog = ({open, onClose}: {open: boolean, onClose: () => void}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('explain');
  const [eraseOn, setEraseOn] = useState(() => previewEraseOn());
  // A new key starts the confirm-it's-you step over, with an empty field.
  const [reauthKey, setReauthKey] = useState(0);
  const [reauthMessage, setReauthMessage] = useState<string | null>(null);
  const [reauthenticating, setReauthenticating] = useState(false);
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEraseOn(previewEraseOn());
    }
  }, [open]);

  // Held open while a request is out, so its answer never lands on a closed
  // dialog (or reopens it a step ahead).
  const close = () => {
    if (deleting || reauthenticating) {
      return;
    }
    setStep('explain');
    setReauthMessage(null);
    setTyped('');
    setError(null);
    onClose();
  };

  const toReauth = (message: string | null) => {
    setReauthMessage(message);
    setReauthKey(key => key + 1);
    setTyped('');
    setError(null);
    setStep('reauth');
  };

  const handleDelete = async () => {
    if (!isDeleteConfirmed(typed) || deleting) {
      return;
    }
    setDeleting(true);
    setError(null);
    let closed: ClosedAccount;
    try {
      closed = await closeMyAccount(navigate);
    } catch (e) {
      setDeleting(false);
      if (isAuthRedirectError(e)) {
        return;
      }
      if (isCloseAccountError(e) && e.status === 403 && e.code === 'reauth_required') {
        toReauth(closeAccountCopy.reauthExpired);
        return;
      }
      if (!isCloseAccountError(e) || e.status !== 409) {
        logError('Delete account failed', {}, e as Error);
      }
      setError(isCloseAccountError(e) ? e.message : closeAccountCopy.genericError);
      return;
    }
    // Stored before signing out: signing out is what takes the app to the
    // sign-in page, which shows the notice.
    rememberClosedNotice(closed.eraseOn);
    await signOutUser().catch(() => undefined);
    navigate('/login', {replace: true});
  };

  const title = step === 'explain'
    ? 'Delete your account?'
    : step === 'reauth' ? "Confirm it's you" : `Type ${DELETE_CONFIRMATION_WORD} to confirm`;

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth aria-labelledby={TITLE_ID}>
      <DialogTitle id={TITLE_ID}>{title}</DialogTitle>
      {step === 'explain' && (
        <>
          <DialogContent>
            <Typography variant="body1">Deleting your SavaQuan account:</Typography>
            <Box component="ul" sx={{pl: 3, my: 1}}>
              {explainBullets(eraseOn).map(bullet => (
                <Typography key={bullet} component="li" variant="body1" sx={{mb: 0.5}}>{bullet}</Typography>
              ))}
            </Box>
            <Typography variant="body2" color="text.secondary">
              If you're the only owner of a team with other people on it, first make someone else Level 5 (Owner / Admin).
            </Typography>
          </DialogContent>
          <DialogActions sx={{p: 2, gap: 1}}>
            <Button onClick={close} color="inherit">Cancel</Button>
            <Button variant="contained" color="error" onClick={() => toReauth(null)}>Continue</Button>
          </DialogActions>
        </>
      )}
      {step === 'reauth' && (
        <ConfirmItsYou
          key={reauthKey}
          initialError={reauthMessage}
          onConfirmed={() => {
            setTyped('');
            setError(null);
            setStep('confirm');
          }}
          onBack={() => setStep('explain')}
          onBusyChange={setReauthenticating}
          recaptchaId={RECAPTCHA_ID}
          confirmColor="error"
        />
      )}
      {step === 'confirm' && (
        <>
          <DialogContent>
            <Typography variant="body1">
              Your account closes now and is permanently deleted on {eraseOn}.
            </Typography>
            <TextField
              label={`Type ${DELETE_CONFIRMATION_WORD}`}
              value={typed}
              onChange={event => setTyped(event.target.value)}
              disabled={deleting}
              autoFocus
              fullWidth
              margin="normal"
              autoComplete="off"
              inputProps={{autoCapitalize: 'characters', spellCheck: false}}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  void handleDelete();
                }
              }}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </DialogContent>
          <DialogActions sx={{p: 2, gap: 1}}>
            <Button onClick={() => toReauth(null)} disabled={deleting} color="inherit">Back</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={!isDeleteConfirmed(typed) || deleting}
              startIcon={deleting ? <CircularProgress size={18} color="inherit"/> : undefined}
            >
              {deleting ? 'Deleting your account…' : 'Delete account'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
